<script lang="ts" setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { ComboboxOptions, Portal } from '@headlessui/vue'
import { type ElementRects, autoUpdate, computePosition, flip, shift, size } from '@floating-ui/vue'
import type { ComplexClassName } from '../../types/common'
import type { FieldComboboxProvider } from '../../types/field'
import { DTFieldComboboxProvider } from '../../keys'
import { classArray } from '../../utils/class'

const props = withDefaults(defineProps<{
  menuClass?: ComplexClassName
  customSizeFn?: (rects: ElementRects, optsRef: HTMLDivElement) => void
}>(), {
  customSizeFn: (rects, optsRef) => {
    Object.assign(optsRef.style, {
      width: `${rects.reference.width}px`,
    })
  },
})

const comboboxProvider = inject<FieldComboboxProvider>(DTFieldComboboxProvider)

const optionsClass = computed<string[]>(() => {
  const cls = [
    'z-50',
    'mt-2',
    'max-h-80',
    'overflow-auto',
    'rounded-lg',
    'bg-white',
    'text-base',
    'leading-none',
    'shadow-lg',
    'border',
    'border-gray-200',
    'divide-y',
    'divide-gray-100',
    'focus:outline-none',
    'empty:hidden',
  ]

  return classArray(props.menuClass ?? '').concat(cls)
})

const target = computed(() => comboboxProvider!.trigger())
const optionsRef = ref<HTMLDivElement>()

const calculatePosition = async () => {
  const { x, y } = await computePosition(
    target.value,
    optionsRef.value!,
    {
      middleware: [
        flip(),
        size({
          apply({ rects }) {
            props.customSizeFn(rects, optionsRef.value!)
          },
        }),
        shift({ padding: 4 }),
      ],
    },
  )

  Object.assign(optionsRef.value!.style, {
    transform: `translate3D(${Math.round(x)}px, ${Math.round(y)}px, 0)`,
  })
}

let cleanAutoUpdate: () => void
onMounted(() => {
  nextTick(() => {
    cleanAutoUpdate = autoUpdate(target.value, optionsRef.value!, calculatePosition)
  })
})
onBeforeUnmount(() => {
  cleanAutoUpdate?.()
})

const handleClick = () => {
  calculatePosition()
}
</script>

<template>
  <Portal>
    <div
      ref="optionsRef"
      class="absolute left-0 top-0 w-max"
    >
      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ComboboxOptions
          :class="optionsClass"
          :unmount="false"
          @click="handleClick"
        >
          <slot />
        </ComboboxOptions>
      </transition>
    </div>
  </Portal>
</template>
